import { useRef, useState } from 'react';
import { api, csvRowErrors, type CsvRowError, type ImportResult } from '../api.ts';
import { copy, copyOutcomeText } from '../clipboard.ts';
import { message, plural } from '../format.ts';

/**
 * Bulk import — the same operation as the form above it, forty clients at a
 * time. It opens from the Clients toolbar beside the single-client form: one
 * client is the rhythm of winning business, a list of forty is administration
 * done once when the deployment is set up and again when a book of business is
 * acquired, and both end in the same place — a client with a consent link
 * nobody has sent yet.
 *
 * A rejected import is a caution, never a failure. Nothing on this screen is a
 * compliance verdict, so nothing on it is red: a mistyped tenant id is a typing
 * mistake, and telling an MSP it is anything more than that trains them to
 * ignore the colour that does mean something.
 *
 * **Every outcome is rendered beside the button that produced it.** The refusals
 * used to go up to the screen's banner, which sits above the client tables at
 * the top of a long page — an operator pressed Import and watched nothing
 * happen. The per-row rejection list was always inline, and it was right: a
 * numbered list of bad rows is only useful next to the text it numbers.
 */

const SAMPLE_CSV =
  'entra tenant id, display name, contact email, contact name\n' +
  'a1b2c3d4-0000-0000-0000-000000000001, Northwind Traders, it@northwind.example, Priya Shah';

const asBlock = (result: ImportResult): string =>
  [
    'Customer\tContact\tConsent link',
    ...result.tenants.map(
      (tenant) => `${tenant.displayName}\t${tenant.contactEmail ?? ''}\t${tenant.consentUrl}`,
    ),
  ].join('\n');

interface ImportClientsProps {
  readonly onImported: () => void;
  /** The screen's live region. The inline messages here are visual only. */
  readonly announce: (text: string) => void;
  /**
   * Whether this deployment has an Entra application. Without one the server
   * refuses the import outright — a list of clients is a list of consent links,
   * and there is nothing to mint them from — so the paste box is replaced by the
   * reason rather than left to be filled in and rejected. Same signal, same
   * words as the single-client form; see `AddClient`.
   */
  readonly entraConfigured: boolean;
}

export const ImportClients = ({ onImported, announce, entraConfigured }: ImportClientsProps) => {
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [rowErrors, setRowErrors] = useState<CsvRowError[] | null>(null);
  const [rejected, setRejected] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [sentAll, setSentAll] = useState(false);
  const [recordFailure, setRecordFailure] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const blockRef = useRef<HTMLTextAreaElement>(null);

  const submit = async () => {
    setBusy(true);
    setResult(null);
    setRowErrors(null);
    setFailure(null);
    setCopyStatus(null);
    setRecordFailure(null);
    setSentAll(false);

    try {
      const response = await api.importTenantsCsv(csv);
      setResult(response);
      onImported();
      announce(
        `Imported: ${plural(response.created, 'tenant')} created, ${response.updated} updated. The consent links are ready to copy.`,
      );
    } catch (cause) {
      const rows = csvRowErrors(cause);
      if (rows) {
        // Hold the text that was rejected, not the live textarea: the user is
        // about to edit it, and the row numbers only mean anything against the
        // version the server actually read.
        setRejected(csv);
        setRowErrors(rows);
        announce(`Import rejected. ${plural(rows.length, 'row')} could not be read.`);
      } else {
        const text = message(cause);
        setFailure(text);
        announce(`That did not happen. ${text}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!result) return;
    const outcome = await copy(asBlock(result), blockRef.current);
    const text = copyOutcomeText(outcome, plural(result.tenants.length, 'consent link'));
    setCopyStatus(text);
    announce(text);
  };

  const markAllSent = async () => {
    if (!result) return;
    setMarking(true);
    setRecordFailure(null);

    // Sequential on purpose: forty parallel writes against a customer's tenant
    // list buys nothing a person can perceive and makes a partial failure much
    // harder to describe.
    const failed: string[] = [];
    for (const tenant of result.tenants) {
      try {
        await api.markInvited(tenant.id);
      } catch {
        failed.push(tenant.displayName);
      }
    }

    setMarking(false);
    setSentAll(failed.length === 0);
    onImported();

    const done = result.tenants.length - failed.length;
    announce(
      failed.length === 0
        ? `${plural(done, 'customer')} marked as sent.`
        : `${plural(done, 'customer')} marked as sent. Could not record: ${failed.join(', ')}.`,
    );
    if (failed.length > 0) setRecordFailure(`Could not record as sent: ${failed.join(', ')}.`);
  };

  const lines = rejected.replace(/\r\n/g, '\n').split('\n');

  return (
    <section className="card admin-import" aria-labelledby="admin-import-heading">
      <div className="card-head">
        <h2 id="admin-import-heading">Import a client list</h2>
        <p className="note">Adding many clients at once</p>
      </div>

      <div className="card-body">
        {entraConfigured ? (
          <>
            <div className="field">
              <label htmlFor="csv">Paste CSV</label>
              <textarea
                id="csv"
                rows={6}
                className="mono admin-csv"
                value={csv}
                aria-describedby="csv-help"
                aria-invalid={rowErrors ? true : undefined}
                onChange={(event) => setCsv(event.target.value)}
                placeholder={SAMPLE_CSV}
              />
              <span className="hint" id="csv-help">
                One client per line: entra tenant id, display name, contact email, contact name. A
                header row is optional; the contact columns may be left empty. Re-importing a tenant
                id updates that client rather than duplicating it.
              </span>
            </div>

            <div className="toolbar">
              <button
                type="button"
                className="btn btn-primary"
                aria-busy={busy || undefined}
                onClick={() => void submit()}
                disabled={busy || !csv.trim()}
              >
                {busy ? 'Importing…' : 'Import'}
              </button>
            </div>

            {failure !== null && <p className="error admin-import-error">{failure}</p>}
          </>
        ) : (
          <div className="callout">
            <span className="callout-title">
              Clients cannot be imported until an Entra application is registered
            </span>
            A list of forty clients is a list of forty consent links, one for each client&rsquo;s
            Global Administrator to open, and every one of them is issued by this
            deployment&rsquo;s own Entra application. Until one is registered and its details are in
            the server environment, there is nothing to issue them from. Whoever administers this
            deployment can register it — the procedure is in{' '}
            <span className="mono">docs/ONBOARDING.md</span>.
          </div>
        )}

        {rowErrors && (
          <div className="callout callout-caution admin-import-rows">
            <span className="callout-title">
              Nothing was imported — {plural(rowErrors.length, 'row')} could not be read
            </span>
            <p className="admin-import-rows-body">
              The rows below are exactly what the server read, numbered as it numbered them. Correct
              the marked ones and import again; nothing was created, so importing the whole list a
              second time duplicates nothing.
            </p>
            <ol className="admin-lines">
              {lines.map((line, index) => {
                const problem = rowErrors.find((entry) => entry.row === index + 1);
                return (
                  <li key={index} className={problem ? 'admin-line admin-line-bad' : 'admin-line'}>
                    <span className="admin-lineno num">{index + 1}</span>
                    <span className="mono admin-linetext">{line || ' '}</span>
                    {problem && <span className="admin-line-reason">{problem.reason}</span>}
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {result && (
          <div className="admin-import-result">
            <p className="admin-import-headline">
              <span className="num">{result.created}</span> created,{' '}
              <span className="num">{result.updated}</span> updated. Batch{' '}
              <span className="mono">{result.batch}</span>.
            </p>

            <div className="callout admin-import-notice">
              <span className="callout-title">Nothing has been sent yet — that part is yours</span>
              DriftWatch never emails your clients. Copy the block below into your own mail merge or
              ticketing system, send it from your own mailbox, then record that you have sent it —
              here, or one client at a time on the Awaiting consent tab — so the list of who is
              waiting knows who is waiting on whom.
            </div>

            <div className="field">
              <label htmlFor="links">Consent links to send</label>
              <textarea
                id="links"
                ref={blockRef}
                rows={Math.min(10, result.tenants.length + 1)}
                className="mono admin-import-block"
                readOnly
                value={asBlock(result)}
              />
              <span className="hint">
                One row per client — customer, contact, consent link — separated by tabs, so it
                pastes straight into a spreadsheet.
              </span>
            </div>

            <div className="toolbar">
              <button type="button" className="btn btn-primary" onClick={() => void copyAll()}>
                Copy all links
              </button>
              <button
                type="button"
                className="btn btn-quiet"
                aria-busy={marking || undefined}
                onClick={() => void markAllSent()}
                disabled={marking || sentAll}
              >
                {marking
                  ? 'Recording…'
                  : sentAll
                    ? 'Recorded as sent'
                    : `Mark all ${result.tenants.length} as sent`}
              </button>
            </div>
            {copyStatus && <p className="status-inline">{copyStatus}</p>}
            {recordFailure !== null && <p className="error">{recordFailure}</p>}
          </div>
        )}
      </div>
    </section>
  );
};
